import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const isMac = () => /Mac|iPhone|iPad|iPod/.test(navigator.platform);

interface TimelineHeaderProps {
  fileName?: string | null;
  onAddTimeline?: () => void;
  addButtonLabel?: string;
  onSave?: () => void;
  onSaveAs?: () => void;
  onNewFile?: () => void;
  onOpenFile?: () => void;
}

export const TimelineHeader = ({
  fileName,
  onAddTimeline,
  addButtonLabel = 'Section',
  onSave,
  onSaveAs,
  onNewFile,
  onOpenFile,
}: TimelineHeaderProps) => {
  const [fileMenuOpen, setFileMenuOpen] = useState(false);
  const fileMenuRef = useRef<HTMLDivElement>(null);
  const mod = useMemo(() => isMac() ? '⌘' : 'Ctrl', []);
  const { t } = useTranslation();

  // Close file menu on outside click
  useEffect(() => {
    if (!fileMenuOpen) return;
    const handler = (e: Event) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(e.target as Node)) {
        setFileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fileMenuOpen]);

  return (
    <div className="flex items-center gap-3">
      {/* App icon - click to go home */}
      <Link
        to="/timelines"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors"
        onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
        }}
        onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <img src="/favicon.svg" alt="Timeline" className="h-6 w-6" />
      </Link>

      {/* File name - click to save as */}
      <button
        type="button"
        className="min-w-0 cursor-pointer rounded-md px-1.5 py-0.5 transition-colors"
        style={{ backgroundColor: 'transparent' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        onClick={() => onSaveAs?.()}
        title={t('file.saveAsTitle')}
      >
        <span
          className="truncate text-xs select-none"
          style={{ color: fileName ? 'var(--color-text-secondary)' : 'var(--color-text-quaternary)' }}
        >
          {fileName || t('file.untitled')}
        </span>
      </button>

      {/* Separator */}
      <div className="h-4 w-px" style={{ backgroundColor: 'var(--color-border)' }} />

      {/* File menu */}
      <div className="relative" ref={fileMenuRef}>
        <button
          type="button"
          className="flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
          style={{
            color: fileMenuOpen ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
            backgroundColor: fileMenuOpen ? 'var(--color-bg-hover)' : 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            if (!fileMenuOpen) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }
          }}
          onClick={() => setFileMenuOpen((prev) => !prev)}
        >
          {t('file.file')}
        </button>
        {fileMenuOpen && (
          <div
            className="absolute left-0 top-full z-50 mt-1 min-w-[220px] overflow-hidden rounded-lg border py-1 shadow-lg whitespace-nowrap"
            style={{
              backgroundColor: 'var(--color-bg-surface)',
              borderColor: 'var(--color-border)',
            }}
          >
            {onNewFile && (
              <FileMenuItem
                label={t('file.new')}
                onClick={() => { setFileMenuOpen(false); onNewFile(); }}
              />
            )}
            {onOpenFile && (
              <FileMenuItem
                label={t('file.open')}
                onClick={() => { setFileMenuOpen(false); onOpenFile(); }}
              />
            )}
            {(onNewFile || onOpenFile) && (onSave || onSaveAs) && (
              <div className="my-1 h-px" style={{ backgroundColor: 'var(--color-border)' }} />
            )}
            {onSave && (
              <FileMenuItem
                label={t('file.save')}
                shortcut={`${mod}+S`}
                onClick={() => { setFileMenuOpen(false); onSave(); }}
              />
            )}
            {onSaveAs && (
              <FileMenuItem
                label={t('file.saveAs')}
                shortcut={`${mod}+Shift+S`}
                onClick={() => { setFileMenuOpen(false); onSaveAs(); }}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* Add section button */}
      {onAddTimeline && (
        <button
          type="button"
          className="flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
          style={{
            color: 'var(--color-text-secondary)',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }}
          onClick={onAddTimeline}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {addButtonLabel}
        </button>
      )}
    </div>
  );
};

const FileMenuItem = ({
  label,
  shortcut,
  onClick,
}: {
  label: string;
  shortcut?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left text-[11px] transition-colors"
    style={{ color: 'var(--color-text-secondary)' }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
      e.currentTarget.style.color = 'var(--color-text-primary)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
      e.currentTarget.style.color = 'var(--color-text-secondary)';
    }}
    onClick={onClick}
  >
    <span>{label}</span>
    {shortcut && (
      <span
        className="text-[10px]"
        style={{ color: 'var(--color-text-quaternary)' }}
      >
        {shortcut}
      </span>
    )}
  </button>
);
